import { lazy, Suspense } from "react";

const EmailForm = lazy(() => import("./EmailForm"));
import ebookCoverWebp from "@/assets/ebook-cover.webp";
import ebookCoverJpg from "@/assets/ebook-cover.jpg";
import lindaImg from "@/assets/testimonial-linda.jpg";
import karenImg from "@/assets/testimonial-karen.jpg";
import susanImg from "@/assets/testimonial-susan.jpg";
import patriciaImg from "@/assets/testimonial-patricia.jpg";
import debbieImg from "@/assets/testimonial-debbie.jpg";
import Picture from "@/components/ui/picture";

const testimonials = [
  {
    img: lindaImg,
    quote:
      "I feel a little lighter… and a lot more hopeful. My morning coffee finally feels like a moment for me again.",
    name: "Linda, 52, Ohio",
  },
  {
    img: karenImg,
    quote:
      "No pressure, just small steps. That changed everything — even on the busy days between work and the school carpool.",
    name: "Karen, 47, Texas",
  },
  {
    img: susanImg,
    quote:
      "After menopause, nothing felt like mine anymore. This was the first thing in a long time that felt kind. I sleep better, and my evenings feel calmer.",
    name: "Susan, 58, North Carolina",
  },
  {
    img: patriciaImg,
    quote:
      "I used to dread the bathroom mirror. Now my morning walk around the neighborhood is the best part of my day.",
    name: "Patricia, 54, Florida",
  },
  {
    img: debbieImg,
    quote:
      "I'm a grandma of three in a small Iowa town. I didn't need another diet — I needed permission to be gentle with myself. That's exactly what this gave me.",
    name: "Debbie, 61, Iowa",
  },
];

const sections: { title: string; items: string[] }[] = [
  {
    title: "What you'll discover inside",
    items: [
      "A gentle 7-day reset designed for women 45+",
      "A simple daily structure — just 5–10 minutes",
      "Why belly fat changes after 40, in plain words",
      "A calm approach that works with your body",
    ],
  },
  {
    title: "What makes this different",
    items: [
      "No strict diets, no extreme workouts",
      "Focus on hormones, stress, sleep, real life",
      "Built for work, family, energy, emotions",
      "Supportive — never punishing",
    ],
  },
  {
    title: "What you'll learn",
    items: [
      "Why your body changed — and why it's not your fault",
      "How stress and sleep quietly affect belly fat",
      "Why strict diets often backfire after 40",
      "How to approach your body with kindness",
    ],
  },
  {
    title: "Practical support inside",
    items: [
      "10 simple, comforting meal ideas",
      "Easy 5-minute morning + evening routines",
      "A printable daily checklist",
      "A gentle walking + movement plan",
    ],
  },
  {
    title: "What many women notice",
    items: [
      "Feeling lighter and less bloated",
      "More stable energy through the day",
      "Better sleep and calmer mood",
      "A softer relationship with their body",
    ],
  },
  {
    title: "Bonus inside",
    items: [
      "10 nourishing real-life meals",
      "5-minute daily routines",
      "Mindset shifts that change everything",
      "A printable PDF to keep and reuse",
    ],
  },
];

const LeadMagnet = () => (
  <section id="guide" className="bg-secondary/40 py-20 sm:py-28">
    <div className="container px-6">
      <div className="reading-column text-center">
        <p className="font-script text-2xl text-primary">A small gift</p>
        <h2 className="mt-2 font-serif text-3xl text-foreground sm:text-4xl">
          The Free 7-Day Belly Reset Plan
          <span className="block text-foreground/70">for women 45+</span>
        </h2>
        <p className="mt-5 text-lg text-foreground/85">
          If you've felt frustrated, stuck, or invisible — this gentle 7-day reset is the
          soft place to start. No calorie counting. No gym. Just small, doable steps designed
          around a woman's life after 45.
        </p>
        <p className="mt-4 font-serif italic text-foreground/75">
          This is where I would start if I had to begin again.
        </p>

        {/* Short bullets — quick scan for mobile */}
        <ul className="mx-auto mt-8 max-w-md space-y-2 text-left text-foreground/85">
          <li className="flex items-start gap-3">
            <span aria-hidden className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-primary" />
            <span>7-day gentle reset (5–10 min/day)</span>
          </li>
          <li className="flex items-start gap-3">
            <span aria-hidden className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-primary" />
            <span>Simple meals + easy routines</span>
          </li>
          <li className="flex items-start gap-3">
            <span aria-hidden className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-primary" />
            <span>No diets. No pressure.</span>
          </li>
        </ul>
        <p className="mt-3 text-sm italic text-foreground/70">
          See everything inside ↓
        </p>

        {/* Soft testimonials */}
        <p className="mx-auto mt-10 max-w-2xl font-serif italic text-foreground/80">
          You're not the only one who felt this way.
        </p>
        <div className="mx-auto mt-4 grid max-w-3xl gap-4 sm:grid-cols-2">
          {testimonials.map((t, i) => (
            <figure
              key={t.name}
              className={`flex gap-4 rounded-2xl bg-background/70 px-5 py-5 text-left ring-1 ring-border/60 ${
                i === testimonials.length - 1 && testimonials.length % 2 === 1 ? "sm:col-span-2" : ""
              }`}
            >
              <img
                src={t.img}
                alt={`${t.name} portrait`}
                loading="lazy"
                width={768}
                height={768}
                className="h-16 w-16 flex-none rounded-full object-cover ring-1 ring-border/60 sm:h-20 sm:w-20"
              />
              <div>
                <blockquote className="font-serif italic text-foreground/85">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-2 text-sm text-foreground/70">— {t.name}</figcaption>
              </div>
            </figure>
          ))}
        </div>
      </div>

      <div className="mx-auto mt-12 grid max-w-4xl items-center gap-10 md:grid-cols-[300px_1fr]">
        {/* Real ebook cover */}
        <div className="mx-auto w-full max-w-[300px]">
          <Picture
            webp={ebookCoverWebp}
            fallback={ebookCoverJpg}
            alt="The Gentle Reset — A 7-Day Belly Reset for Women 45+ ebook cover"
            loading="lazy"
            className="aspect-[3/4] w-full rounded-[1.25rem] object-cover shadow-md ring-1 ring-border"
          />
          <div className="mt-3 flex flex-col items-center gap-1 text-xs text-foreground/80">
            <span>18 pages · Printable PDF</span>
            <span>Instant access after signup</span>
          </div>
        </div>

        {/* Bullets + form */}
        <div>
          <div className="space-y-6">
            {sections.map((s) => (
              <div key={s.title}>
                <h3 className="font-serif text-lg text-foreground">{s.title}</h3>
                <ul className="mt-2 space-y-2 text-foreground/85">
                  {s.items.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span aria-hidden className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-primary" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-2xl bg-background/60 px-6 py-6 text-center ring-1 ring-border/60">
            <p className="font-serif text-xl text-foreground/90 sm:text-2xl">
              If you're feeling even a little like I did…
              <br />
              this is a gentle place to begin.
            </p>
          </div>

          <p className="mt-6 text-center font-serif text-lg italic text-foreground/80">
            You don't need to figure everything out today.
            <br />
            Just start here.
          </p>

          <p className="mt-4 text-center italic text-foreground/80">
            You'll get the PDF the moment you enter your email.
          </p>

          <div className="mt-6">
            <Suspense fallback={null}>
              <EmailForm variant="guide" buttonLabel="I'd like to start gently" />
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default LeadMagnet;
